import { storeWrapper } from '../storeWrapper.js'
import { throttle, debounce } from '@repo/utils/debounce'
import { ospreyAPI } from '@repo/api/ospreyAPI'
import { useMattersStore } from '@repo/store/useMattersStore'

const initialState = {
  currentMessage: null,
  messageChangeTrigger: 0,
  conversationData: {},
  token: null,
  selectedTab: 'sync',
  matterHistoryEmailConfigurationIds: {},
  htmlEmailIdentification: '',
  plainTextEmailIdentification: '',
  subjectEmailIdentification: ''
}

export const useOutlookAddinStore = storeWrapper('useOutlookAddinStore', (set, get) => ({
  ...initialState,
  incrementMessageChangeTrigger: () => set((state) => ({ messageChangeTrigger: state.messageChangeTrigger + 1 })),
  createNewConversation: async ({ clientNo, matterNo }) => {
    return new Promise((resolve) => {
      throttle({
        id: `createNewConversation-${clientNo}-${matterNo}`,
        callback: async () => {
          const emailConversationId = await ospreyAPI({
            service: 'emails',
            endpoint: 'MatterEmailConversation/Create',
            method: 'POST',
            data: { clientNo, matterNo }
          })

          resolve(emailConversationId)
        }
      })
    })
  },
  addConversationIdToEmail: async ({ messageId, conversationId }) => {
    return new Promise((resolve) => {
      throttle({
        id: `addConversationIdToEmail-${messageId}-${conversationId}`,
        callback: async () => {
          const response = await ospreyAPI({
            service: 'emails',
            endpoint: 'Email/AddConversationIdToEmail',
            method: 'POST',
            sendAsUrlParams: true,
            data: { messageId, conversationId }
          })

          resolve(response)
        }
      })
    })
  },
  addEmailToMatterHistory: async ({
    messageId,
    emailConversationId,
    clientNo,
    matterNo,
    matterHistoryDetailForReceivedEmail,
    matterHistoryDetailForSentEmail,
    matterHistoryFolderId,
    matterHistoryTypeId,
    retentionPolicyId,
    retentionDateType,
    isSaveEmailBody,
    isSaveEmailAttachments,
    isIgnoreInlineImages,
    ignoreInlineImagesBelowKilobytes
   }) => {
    return new Promise((resolve) => {
      debounce({
        id: `addEmailToMatterHistory-${messageId}-${emailConversationId}`,
        delay: 500,
        callback: async () => {
          const response = await ospreyAPI({
            service: 'emails',
            endpoint: 'Email/AddEmailToMatterHistory',
            method: 'POST',
            sendAsUrlParams: true,
            data: {
              MessageId: messageId,
              EmailConversationId: emailConversationId,
              IsSentItem: false,
              ClientNo: clientNo,
              MatterNo: matterNo,
              'MatterHistoryEmailConfiguration.EmailConversationId': emailConversationId,
              'MatterHistoryEmailConfiguration.MatterHistoryDetailForReceivedEmail': matterHistoryDetailForReceivedEmail,
              'MatterHistoryEmailConfiguration.MatterHistoryDetailForSentEmail': matterHistoryDetailForSentEmail,
              'MatterHistoryEmailConfiguration.MatterHistoryFolderId': matterHistoryFolderId,
              'MatterHistoryEmailConfiguration.MatterHistoryTypeId': matterHistoryTypeId,
              'MatterHistoryEmailConfiguration.RetentionPolicyId': retentionPolicyId,
              'MatterHistoryEmailConfiguration.RetentionDateType': retentionDateType,
              'MatterHistoryEmailConfiguration.IsSaveEmailBody': isSaveEmailBody,
              'MatterHistoryEmailConfiguration.IsSaveEmailAttachments': isSaveEmailAttachments,
              'MatterHistoryEmailConfiguration.IsIgnoreInlineImages': isIgnoreInlineImages,
              'MatterHistoryEmailConfiguration.IgnoreInlineImagesBelowKilobytes': ignoreInlineImagesBelowKilobytes
            }
          })

          resolve(response)
        }
      })
    })
  },
  fetchSelectedEmailConversations: async ({ internetMessageId }) => {
    try {
      const data = await ospreyAPI({
        service: 'matters',
        endpoint: 'MatterHistory/GetAllMatterEmailConversationsByInternetMessageId',
        data: { internetMessageId },
        // Failures here are owned by the caller: ReadEmailPage retries + emits telemetry,
        // ComposeEmailPage/AddMatterForm log. Don't also pop ospreyAPI's generic error
        // toast — during ReadEmailPage's retry loop that would mean one toast per attempt.
        suppressErrorToast: true
      })

      const conversationMatters = data.map((conversation) => `${conversation.clientNo}-${conversation.matterNo}`)

      // we need to make sure we have the matter data for each conversation
      conversationMatters?.forEach((conversationClientMatterNo) => {
        useMattersStore.getState().fetchMatter({ clientMatterNo: conversationClientMatterNo })
      })

      set({ conversationData: { ...get().conversationData, [internetMessageId]: data } })
      return data
    } catch (error) {
      const status = error?.status || error?.response?.status || error?.cause?.status

      // The current backend returns 200 [] for "no matching matters", so that case comes
      // through the success path above. Older/on-prem API versions historically returned
      // 404 for the same case — keep treating that as a benign empty result so this
      // add-in stays compatible with them.
      if (status === 404) {
        return []
      }

      // A genuine failure (network / 5xx / timeout / offline). Propagate it rather than
      // returning [] — a [] here masquerades as "no matters", so ReadEmailPage would set
      // itemId and its dedup would then block re-selection of this email from ever
      // retrying. Throwing lets ReadEmailPage's catch run its retry + telemetry path.
      console.error('Error fetching selected email conversations:', error)
      throw error
    }
  },
  fetchMattersByConversationId: async ({ emailConversationId }) => {
    try {
      const data = await ospreyAPI({
        service: 'emails',
        endpoint: 'MatterEmailConversation/GetAllMatterIdByEmailConversationId',
        data: { emailConversationId }
      })

      return data
    } catch (error) {
      console.error('Error fetching selected email conversations by id:', error)
      return
    }
  },
  fetchMatterHistoryConfigurationByConversationId: async ({ emailConversationId }) => {
    try {
      // if we've already fetched the matter history configuration for this conversation, return the existing id from the cache
      const existingMatterHistoryEmailConfigurationId = get().matterHistoryEmailConfigurationIds[emailConversationId]
      if (existingMatterHistoryEmailConfigurationId || existingMatterHistoryEmailConfigurationId === 0) {
        return existingMatterHistoryEmailConfigurationId
      }

      // otherwise, fetch the matter history configuration from the API
      const data = await ospreyAPI({
        service: 'emails',
        endpoint: 'MatterHistoryEmailConfiguration/GetByEmailConversationId',
        data: { emailConversationId }
      })

      // if we didn't get a response, or the response doesn't have a matter history email configuration id, return
      if (!data || !data.matterHistoryEmailConfigurationId) return null

      // otherwise, set the matter history email configuration id in the cache
      set({
        matterHistoryEmailConfigurationIds: {
          ...get().matterHistoryEmailConfigurationIds,
          [emailConversationId]: data.matterHistoryEmailConfigurationId
        }
      })

      return data.matterHistoryEmailConfigurationId
    } catch (error) {
      console.error('Error fetching matter history configuration by conversation id:', error)
      return
    }
  },
  createOrUpdateMatterHistoryEmailConfigurationForConversation: async (data = {}) => {
    if (!data.emailConversationId) return

    return new Promise((resolve) => {
      debounce({
        id: `createOrUpdateMatterHistoryEmailConfigurationForConversation-${data.emailConversationId}`,
        delay: 500,
        callback: async () => {
          const existingMatterHistoryEmailConfigurationId = await get().fetchMatterHistoryConfigurationByConversationId({ emailConversationId: data.emailConversationId })

          let endpoint = 'MatterHistoryEmailConfiguration/Create'
          let method = 'POST'

          if (existingMatterHistoryEmailConfigurationId || existingMatterHistoryEmailConfigurationId === 0) {
            endpoint = 'MatterHistoryEmailConfiguration/Update'
            method = 'PUT'
            data.matterHistoryEmailConfigurationId = existingMatterHistoryEmailConfigurationId
          }

          const response = await ospreyAPI({
            service: 'emails',
            endpoint,
            method,
            data
          })

          resolve(response)
        }
      })
    })
  },
  fetchEmailConfigurationOptions: async () => {
    try {
      const { htmlEmailIdentification, plainTextEmailIdentification, subjectEmailIdentification } = await ospreyAPI({
        service: 'lookups',
        endpoint: 'OspreyConfiguration/GetEmailIdentification'
      })

      set({ htmlEmailIdentification, plainTextEmailIdentification, subjectEmailIdentification})
    } catch (error) {
      console.error('Error fetching email configuration options:', error)
      set({
        htmlEmailIdentification: 'HtmlPElementWithEmailConversationId',
        plainTextEmailIdentification: 'EmailConversationId',
        subjectEmailIdentification: 'NoIdentification'
      })
    }
  },
  fetchEmailIdentificationTextForEmailBody: async ({ clientNo, matterNo, emailConversationId, isHtml = true }) => {
    try {
      const data = await ospreyAPI({
        service: 'emails',
        endpoint: 'Email/GetEmailIdentificationTextForBody',
        data: { clientNo, matterNo, emailConversationId, isHtml }
      })

      return data
    } catch (error) {
      console.error('Error fetching selected email identification text for email body:', error)
      return
    }
  },
  fetchEmailIdentificationTextForSubject: async ({ clientNo, matterNo, emailConversationId }) => {
    try {
      const data = await ospreyAPI({
        service: 'emails',
        endpoint: 'Email/GetEmailIdentificationTextForSubject',
        data: { clientNo, matterNo, emailConversationId }
      })

      return data
    } catch (error) {
      console.error('Error fetching selected email identification text for subject:', error)
      return
    }
  },
  fetchInternetMessageIdFromGraphApiId: async ({ graphApiMessageId }) => {
    try {
      const data = await ospreyAPI({
        service: 'emails',
        endpoint: 'Email/GetInternetMessageId',
        data: { graphApiMessageId }
      })

      return data
    } catch (error) {
      // Propagate, don't swallow. The caller (findInternetMessageId in ReadEmailPage)
      // needs to distinguish "request failed" (e.g. 401 — retry won't help, terminate)
      // from "request succeeded but backend has no data for this id" (retry might help on
      // a later attempt as indexing catches up). Returning undefined for both conflated
      // them and made it impossible to short-circuit auth failures or surface a status.
      console.error('Error fetching internetMessageId from Graph API id:', error)
      throw error
    }
  },
  reset: () => {
    const { token } = get()
    set({ ...initialState, token })
  }
}))
